import React, { useContext, useEffect, useRef, useState } from "react";
import {
  PiCaretLeftLight,
  PiCaretRightLight,
  PiListLight,
  PiXLight,
} from "react-icons/pi";
import { Link, useNavigate, useParams } from "react-router-dom";
import client from "../../api/client";
import { GlobalContext } from "../../context";
import ErrorPage from "../../page/ErrorPage";
import { FetchCurrentWatchingCourse, addObjectToFirebase } from "../../utility";
import LoadingSpinner from "../LoadingSpinner";
import CourseMenu from "./components/CourseMenu";
import { toast } from "react-toastify";
import { useIsMobile } from "../hooks/useIsMobile";
import VideoPlayer from "./Videoplayer";

const SIDEBAR_MIN  = 200;
const SIDEBAR_MAX  = 520;
const SIDEBAR_DEFAULT = 320;

export default function Course() {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [courses, setCourses]                   = useState();
  const [loading, setLoading]                   = useState(false);
  const [videoUrl, setVideoUrl]                 = useState("");
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(null);
  const [selectedLessonTitle, setSelectedLessonTitle] = useState("");

  /* ── Sidebar state ──────────────────────────────────────────────────────── */
  const [mobileOpen,   setMobileOpen]   = useState(false);  // mobile drawer
  const [collapsed,    setCollapsed]    = useState(false);   // desktop collapse
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const [, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: SIDEBAR_DEFAULT });

  const { authState: { data, loggedIn } } = useContext(GlobalContext);
  const user   = data?.user;
  const userId = user?.id ?? null;
  const lessonId = courses?.lesson[selectedLessonIndex]?.uuid;

  useIsMobile();

  /* ── Auth guard ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!loggedIn) {
      toast.error("You must be logged in before accessing this page");
      navigate("/sign-in");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Load course data ───────────────────────────────────────────────────── */
  const loadData = async () => {
    setLoading(true);
    try {
      const res = (await client.get(`/courses/online/online-courses/details/${uuid}`)).data;
      setCourses(res);
    } catch (error) {
      console.log("error fetching course details", error.response);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (loggedIn) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  /* ── Lesson selection ───────────────────────────────────────────────────── */
  const handleVideoClick = (lessonTitle, lessonDescription, videoLink, index) => {
    setSelectedLessonTitle(lessonTitle);
    setSelectedLessonIndex(index);
    setVideoUrl(videoLink);
    setMobileOpen(false);
  };

  const AddWatchCourse = (lessonInfo) =>
    addObjectToFirebase(uuid, user?.id?.toString(), courses.course_id?.toString(), {
      ...courses,
      lessonInfo,
    });

  const currentWatchingStatus = async () => {
    const res = await FetchCurrentWatchingCourse(user?.id?.toString(), uuid);
    if (res.status) {
      handleVideoClick(
        res.data?.lessonInfo?.title,
        res.data?.lessonInfo?.description,
        res.data?.lessonInfo?.link,
        res.data?.lessonInfo?.index
      );
    } else {
      handleVideoClick(
        courses?.lesson[0]?.title,
        courses?.lesson[0]?.description,
        courses?.lesson[0]?.course_link,
        0
      );
    }
  };

  useEffect(() => {
    currentWatchingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, userId, courses]);

  /* ── Drag-to-resize ─────────────────────────────────────────────────────── */
  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: sidebarWidth };
    setIsDragging(true);
    document.body.style.cursor   = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (e) => {
      const delta    = e.clientX - dragRef.current.startX;
      const newWidth = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, dragRef.current.startWidth + delta));
      setSidebarWidth(newWidth);
    };

    const onUp = () => {
      setIsDragging(false);
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  };

  /* ── Lesson list (shared between mobile + desktop) ──────────────────────── */
  const LessonList = () => (
    <>
      {courses?.lesson?.length > 0 &&
        courses.lesson.map((lesson, index) => (
          <CourseMenu
            course_id={uuid}
            onClick={() => {
              if (lesson?.course_link != null) {
                AddWatchCourse({
                  title: lesson.title,
                  description: lesson.course_description,
                  link: lesson.course_link,
                  index,
                });
                handleVideoClick(lesson.title, lesson.course_description, lesson.course_link, index);
              } else {
                toast.warning("Purchase the paid course to enjoy this course");
                navigate("/online-course/5e45959e-6d17-4bfe-b568-2cb3ade6851e");
              }
            }}
            {...lesson}
            key={index}
            index={index}
            selectedLessonIndex={selectedLessonIndex}
          />
        ))}
    </>
  );

  /* ── Render ─────────────────────────────────────────────────────────────── */
  if (loading) return <LoadingSpinner />;
  if (!user)   return <ErrorPage error={{ message: "Unauthorised — please login or refresh" }} />;

  return (
    <div className="flex h-full w-full overflow-hidden">

      {/* ── DESKTOP SIDEBAR ──────────────────────────────────────────────── */}
      <aside
        style={{ width: collapsed ? 0 : sidebarWidth }}
        className="hidden md:flex flex-col h-full bg-white border-r border-gray-200 flex-shrink-0 relative overflow-hidden"
      >
        {/* Header */}
        <p className="text-black px-4 py-3 text-base font-semibold border-b border-gray-100 flex-shrink-0">
          Course Content
        </p>

        {/* Scrollable lesson list */}
        <div className="flex-1 overflow-y-auto">
          <LessonList />
        </div>

        {/* ── Resize handle + toggle button ──────────────────────────────── */}
        <div
          onMouseDown={startDrag}
          className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize
            bg-gray-100 hover:bg-[#F05A19]/40 transition-colors z-20"
        >
          {/* Toggle chevron centered on the handle */}
          <button
            onMouseDown={(e) => e.stopPropagation()}   /* don't start drag */
            onClick={() => setCollapsed(true)}
            title="Collapse panel"
            className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2
              w-5 h-10 bg-white border border-gray-200 rounded-full shadow-md
              flex items-center justify-center text-gray-500
              hover:bg-[#F05A19] hover:text-white hover:border-[#F05A19]
              transition-colors z-30"
          >
            <PiCaretLeftLight size={12} />
          </button>
        </div>
      </aside>

      {/* ── EXPAND BUTTON (shown when collapsed on desktop) ──────────────── */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          title="Expand panel"
          className="hidden md:flex flex-col items-center justify-center
            w-5 h-10 my-auto flex-shrink-0
            bg-white border border-gray-200 rounded-r-full shadow-md
            text-gray-500 hover:bg-[#F05A19] hover:text-white hover:border-[#F05A19]
            transition-colors z-20 self-center"
          style={{ position: "relative" }}
        >
          <PiCaretRightLight size={12} />
        </button>
      )}

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto min-w-0">

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-16 left-0 right-0 z-[60] flex justify-between items-center
          bg-white border-b border-gray-100 px-4 py-2">
          <button
            onClick={() => setMobileOpen(true)}
            className="flex items-center gap-2 text-[#227AFF] text-sm"
          >
            <PiListLight />
            <span>COURSE MENU</span>
          </button>
        </div>

        {/* Content with top offset on mobile for the bar above */}
        <div className="mt-12 md:mt-0 px-4 md:px-6">
          {/* Nav row */}
          <div className="flex justify-between items-center py-3">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#227AFF] transition-colors"
            >
              <PiCaretLeftLight />
              Back To Course
            </button>
            <Link
              to="/all-courses"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#227AFF] transition-colors"
            >
              Go To All Courses
              <PiCaretRightLight />
            </Link>
          </div>

          {/* Lesson title */}
          {selectedLessonTitle && (
            <p className="mb-3 font-semibold uppercase text-sm tracking-wide text-gray-800">
              {selectedLessonTitle}
            </p>
          )}

          {/* Player */}
          {videoUrl !== "" ? (
            <VideoPlayer uuid={uuid} lessonId={lessonId} videoUrl={videoUrl} />
          ) : (
            <img src={courses?.images} alt="course" loading="lazy" className="w-full rounded-lg" />
          )}
        </div>
      </main>

      {/* ── MOBILE DRAWER OVERLAY ─────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[70] flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer */}
          <div className="relative z-10 w-[85vw] max-w-sm h-full bg-white flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="font-semibold text-gray-800">Course Content</p>
              <button
                onClick={() => setMobileOpen(false)}
                className="text-gray-500 hover:text-gray-800 p-1"
              >
                <PiXLight size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <LessonList />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
